import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Spin, Typography, Select } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const { Title } = Typography;
const { Option } = Select;

interface UserFormData {
  name: string;
  email: string;
  password?: string;
  role: 'admin' | 'operator' | 'staff';
}

const UserForm: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  
  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      message.error('您没有权限访问此页面');
      navigate('/dashboard');
      return;
    }
    
    if (id) {
      setIsEdit(true);
      fetchUserData(id);
    }
  }, [id, currentUser, navigate]);
  
  const fetchUserData = async (userId: string) => {
    setInitialLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/${userId}`);
      if (response.data.success) {
        const userData = response.data.data;
        // 不显示密码字段
        form.setFieldsValue({
          name: userData.name,
          email: userData.email,
          role: userData.role,
        });
      } else {
        message.error(response.data.message || '获取用户数据失败');
        navigate('/users');
      }
    } catch (error: any) {
      message.error('获取用户数据时出错: ' + (error.response?.data?.message || error.message));
      navigate('/users');
    } finally {
      setInitialLoading(false);
    }
  };
  
  const onFinish = async (values: UserFormData) => {
    setLoading(true);
    try {
      let response;
      if (isEdit) {
        // 如果密码为空，就不更新密码
        if (!values.password) {
          const { password, ...dataWithoutPassword } = values;
          response = await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${id}`, dataWithoutPassword);
        } else {
          response = await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${id}`, values);
        }
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/users`, values);
      }
      
      if (response.data.success) {
        message.success(response.data.message || (isEdit ? '用户更新成功' : '用户创建成功'));
        navigate('/users');
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        // 显示表单验证错误
        error.response.data.errors.forEach((err: any) => {
          message.error(`${err.path}: ${err.message}`);
        });
      } else {
        message.error('操作失败: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="加载用户数据..." />
      </div>
    );
  }
  
  return (
    <div>
      <Title level={2}>{isEdit ? '编辑用户' : '添加用户'}</Title>
      
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            role: 'staff',
          }}
        >
          <Form.Item
            name="name"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 2, message: '用户名长度不能少于2个字符' },
            ]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' },
            ]}
          >
            <Input placeholder="请输入邮箱" disabled={isEdit} />
          </Form.Item>
          
          <Form.Item
            name="password"
            label={isEdit ? '密码 (留空表示不修改)' : '密码'}
            rules={[
              { required: !isEdit, message: '请输入密码' },
              { min: 6, message: '密码长度不能少于6个字符' },
            ]}
          >
            <Input.Password placeholder={isEdit ? '留空表示不修改密码' : '请输入密码'} />
          </Form.Item>
          
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              <Option value="admin">管理员</Option>
              <Option value="operator">运营</Option>
              <Option value="staff">员工</Option>
            </Select>
          </Form.Item>
          
          <Form.Item className="form-actions">
            <Button onClick={() => navigate('/users')}>
              取消
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEdit ? '更新' : '添加'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default UserForm; 